import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  view,
  blocks,
  dialogs,
  site,
  customization,
  blockTemplates,
  textFormatting,
  hdrm,
  transactions,
  readOnly,
  errors,
  cssGenerator,
} from '@yola/ws-sdk';
import designAutomation from 'src/js/modules/design-automation';
import onlineStore from 'src/js/modules/onlinestore';
import scroller from 'src/js/modules/scroller';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import store from 'src/js/store';
import getAcceptableVariations from '../helpers/get-acceptable-variations';
import getBlocksWithDependenciesByCategory from '../helpers/get-blocks-with-dependencies-by-category';
import BlockSettingsDialogContainer from './block-settings-dialog-container';
import getVisibleOptions from '../helpers/get-visible-options';
import getBlockIdRulesMap from '../helpers/get-block-id-rules-map';
import generateBlockCustomOptionsStylesheet from '../helpers/generate-block-custom-options-stylesheet';
import BlockSettingsContext from '../contexts/block-settings-context';
import blockSettingsHelpers from '../helpers/block-settings';
import layoutSwitchingHelpers from '../helpers/layout-switching';
import trackBlockSettingsOptionClicked from '../trackers/track-block-settings-option-clicked';
import { ENTER_KEY } from '../constants/key-codes';
import { HIDE_ELEMENT_TRIGGER_ID } from '../constants/trigger-ids';
import {
  DISPLAY_ON_BLOCK_LAYOUT,
  LIVE_PREVIEW_MOCKED_ID,
  BLOCK_SETTINGS_LAYOUT_TAB_ID,
} from '../constants/common';
import { ATTRIBUTES_TO_MARK_AS_USER_MODIFIED } from '../constants/display-options';
import { SET } from '../constants/attribute-operations';
import {
  BACKGROUND_IMAGE_ATTRIBUTE,
  BACKGROUND_PLACEHOLDER_ATTRIBUTE,
  BACKGROUND_HIDDEN_ATTRIBUTE,
} from '../constants/block-background-attributes';
import getOriginalBlockData from '../helpers/get-original-block-data';
import getSelectedLayoutVariationId from '../helpers/get-selected-layout-variation-id';
import getSortBlockVariationId from '../helpers/get-sort-block-variation-id';
import cancelBlockSettingsChanges from '../helpers/cancel-block-settings-changes';
import saveBlockSettingsChanges from '../helpers/save-block-settings-changes';
import {
  CONTAINER_WIDTH_CLASS,
  CONTAINER_WIDTH_OPTION_ID,
  CONTAINER_WIDTH_PRESETS,
  CONTAINER_WIDTH_WIDE_PX,
  CONTAINER_WIDTH_NARROW_PX,
  CONTAINER_SELECTOR,
} from '../constants/container-width';
import trackers from '../trackers';
import getActiveTabId from '../helpers/get-active-tab-id';
import getUpdatedDisplayOptions from '../helpers/get-updated-display-options';
import convertAttributeOperationToAction from '../helpers/convert-attribute-operation-to-action';
import convertChangedOptionToOperation from '../helpers/convert-changed-option-to-operation';
import { applyHiddenClassTokens } from '../helpers/hidden-class-tokens';
import * as helpers from '../helpers/temporarily-reveal-hidden-block';

const isBackgroundOptionAvailable = (blockNode) =>
  blockNode.hasAttribute(BACKGROUND_IMAGE_ATTRIBUTE) ||
  blockNode.hasAttribute(BACKGROUND_PLACEHOLDER_ATTRIBUTE) ||
  blockNode.hasAttribute(BACKGROUND_HIDDEN_ATTRIBUTE);

const getInitialContainerWidthCustomValue = (displayOption) => {
  if (displayOption.customValue) return displayOption.customValue;
  if (displayOption.value === CONTAINER_WIDTH_PRESETS.WIDE) return CONTAINER_WIDTH_WIDE_PX;
  return CONTAINER_WIDTH_NARROW_PX;
};

const { getBlockItemsForRendering, preprocessBlockLayout } = layoutSwitchingHelpers;

const {
  createOptionalChildren,
  createBackgroundOption,
  getCaptions,
  getOptionTargetElement,
  trackWsBlockContent,
  updateDependentWhiteSpaceOptions,
  updateSurfaceDependentOptions,
  buildContainerWidthOption,
  getContainerWidthClassValue,
} = blockSettingsHelpers;

const { updateStrategies } = hdrm.constants;
const { breakpointNames } = cssGenerator.constants;

const LIVE_STRATEGY_OPTIONS = { strategy: updateStrategies.UPDATE_LIVE_ONLY };

class BlockSettingsContainer extends React.Component {
  static toggleMultiSurfaces(isEnabled, item) {
    if (isEnabled) {
      const targetSurface = item.options.find((option) => option.value === item.surfaces[0]);

      item.onClick(item, (targetSurface || item.options[0]).value);
    } else {
      item.onClick(item, null);
    }
  }

  constructor(props) {
    super(props);
    const {
      blockId: originBlockId,
      element: originBlockNode,
      blockItems,
      tabId,
      getSharedData,
      refId: originRefId,
      blockTemplateCollection,
    } = props;

    this.sharedData = getSharedData();

    const {
      offsetX = 0,
      offsetY = 0,
      isDirtyOptions = false,
      isDirtyChildren = false,
      optionalChildren = null,
      displayOptions = null,
      backgroundOption = null,
      cachedBackgroundOption = null,
      refId = originRefId,
      optionalChildrenState = [],
      compiledNodesCache = new Map(),
      destinationBlockNode = null,
      variationId = null,
      originalBlock = null,
      blockId = originBlockId,
      blockNode = originBlockNode,
      sortBlockVariationId = null,
      selectedPreviewNode = null,
      selectedLayoutVariationId = null,
      itemsWithDependencies = null,
      isOnlineStoreBlock,
      scrollTop = 0,
      hiddenClassTokensSnapshot = null,
    } = this.sharedData;

    const usedTextColorOptions = textFormatting.helpers.getElementCustomColorVariables(blockNode);

    const block = blocks.accessors.getBlockByElement(blockNode);

    // Block dependencies
    this.refId = refId || view.accessors.getLiveElementId(blockNode);

    this.intermediateBlockId = blockId; // Block type ID
    this.blockElement = blockNode; // Block node element
    this.blockElementId = blockNode.id; // ID of ws-block element
    this.isOnlineStoreBlock =
      isOnlineStoreBlock || onlineStore.verifiers.isOnlineStoreBlock(blockNode);

    this.destinationBlockNode = destinationBlockNode;
    this.hiddenClassTokensSnapshot = hiddenClassTokensSnapshot;

    // Styles dependencies
    const textColorStylesheet = textFormatting.helpers.getCustomTextColorsStylesheet();
    const globalStylesRule = customization.helpers.getCustomGlobalStylesRootRule();

    this.blocksPreviewStylesheet = generateBlockCustomOptionsStylesheet(
      textColorStylesheet,
      globalStylesRule,
      usedTextColorOptions,
      this.blockElementId
    );
    this.blockIdRulesMap = getBlockIdRulesMap(textColorStylesheet, this.blockElementId);
    this.usedCustomColorOptions =
      Boolean(usedTextColorOptions.length) && this.blockIdRulesMap && this.blockIdRulesMap.size > 0;

    // Origin block data
    this.originalBlock = getOriginalBlockData(originalBlock, {
      refId: this.refId,
      blockNode,
      blockItems,
      blockId,
    });

    const blockVariationWithCategories = block.variations?.find(
      (variation) => this.originalBlock.blockVariationId === variation.id && variation.category
    );
    const category = blockVariationWithCategories?.category || block.category;

    this.category = category;
    this.selectedPreviewNode =
      selectedPreviewNode || this.originalBlock.staticBlockNode.cloneNode(true);

    this.colorPalette = customization.accessors.getCurrentColorPalette();

    this.itemsWithDependencies =
      itemsWithDependencies ||
      getAcceptableVariations(
        getBlocksWithDependenciesByCategory(blockItems, category, blockTemplateCollection),
        DISPLAY_ON_BLOCK_LAYOUT
      );

    const activeTabId = getActiveTabId({
      blockId: this.intermediateBlockId,
      tabId,
      itemsWithDependencies: this.itemsWithDependencies,
    });

    this.state = {
      offsetX,
      offsetY,
      selectedLayoutVariationId: getSelectedLayoutVariationId(
        this.originalBlock,
        selectedLayoutVariationId
      ),
      sortBlockVariationId: getSortBlockVariationId(this.originalBlock, sortBlockVariationId),
      optionalChildren,
      isDirtyOptions,
      isDirtyChildren,
      blockNode,
      blockId: this.intermediateBlockId,
      variationId: variationId || this.originalBlock.blockVariationId,
      displayOptions,
      backgroundOption,
      cachedBackgroundOption,
      timestamp: Date.now(),
      tabId: activeTabId,
      isAutomationInProgress: false,
    };

    this.switchRadioOption = this.switchRadioOption.bind(this);
    this.switchCheckboxOption = this.switchCheckboxOption.bind(this);
    this.switchToggleOption = this.switchToggleOption.bind(this);
    this.switchChildDisplay = this.switchChildDisplay.bind(this);
    this.switchSliderOption = this.switchSliderOption.bind(this);
    this.afterSwitchSliderOption = this.afterSwitchSliderOption.bind(this);
    this.switchSurface = this.switchSurface.bind(this);
    this.switchOnlineStoreCategory = this.switchOnlineStoreCategory.bind(this);
    this.prepareData = this.prepareData.bind(this);
    this.setOffsets = this.setOffsets.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.scrollToBlock = this.scrollToBlock.bind(this);
    this.preprocessBlockLayoutDecorator = this.preprocessBlockLayoutDecorator.bind(this);
    this.getUpdatedOptions = this.getUpdatedOptions.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);
    this.displayBlockLayout = this.displayBlockLayout.bind(this);
    this.updateSharedData = this.updateSharedData.bind(this);
    this.modifyDisplayOption = this.modifyDisplayOption.bind(this);
    this.handleAutomationStart = this.handleAutomationStart.bind(this);
    this.handleAutomationEnd = this.handleAutomationEnd.bind(this);
    this.switchContainerWidthValue = this.switchContainerWidthValue.bind(this);
    this.switchContainerWidthCustomValue = this.switchContainerWidthCustomValue.bind(this);
    this.commitContainerWidthCustomValue = this.commitContainerWidthCustomValue.bind(this);

    this.captions = getCaptions();
    this.optionalChildrenState = optionalChildrenState;
    this.compiledNodesCache = compiledNodesCache;
    this.scrollContainerRef = React.createRef();
    this.shouldPrepareDataAfterEmptyAutomation = true;
    this.preservedScrollTop = scrollTop;
  }

  componentDidMount() {
    const blockSettings = blocks.accessors.getBlockSettings(
      this.intermediateBlockId,
      this.colorPalette
    );
    const { tabId, variationId } = this.state;
    const { optionsSetup } = this.props;
    const { optionalChildren: optionalChildrenSetup } = optionsSetup;
    const childrenSetupIds = Object.keys(optionalChildrenSetup || {});

    this.prepareData(blockSettings, () => {
      const { optionalChildren } = this.state;

      if (optionalChildren) {
        optionalChildren.forEach((child) => {
          if (childrenSetupIds.includes(child.id)) {
            this.switchChildDisplay(!child.isEnabled, child, { highlighted: true });
          }
        });
      }

      this.restoreScrollPosition();
    });

    trackers.trackBlockSettingsDialogDisplayed({
      tabId,
      blockId: this.intermediateBlockId,
      blockVariationId: variationId,
      triggerId: childrenSetupIds.length ? HIDE_ELEMENT_TRIGGER_ID : null,
    });

    const isOpenedInitially = !Object.keys(this.sharedData).length;

    if (isOpenedInitially) {
      const transaction = transactions.helpers.getTransactionSession();

      if (transaction.id) {
        if (transaction.context === transactions.transactionTypes.LIVE_DOCUMENT) {
          transactions.helpers.releaseTransactionSession(
            transactions.transactionTypes.LIVE_DOCUMENT
          );
        } else {
          transactions.helpers.closeTransaction(transactions.transactionTypes.STATIC_DOCUMENT);
        }
      }

      transactions.helpers.dedicateTransactionSession(transactions.transactionTypes.LIVE_DOCUMENT);
      this.hiddenClassTokensSnapshot = helpers.temporarilyRevealBlock(this.refId);
    }

    designAutomation.operations.subscribe(
      designAutomation.eventTypes.AUTOMATION_STARTED,
      this.handleAutomationStart
    );
    designAutomation.operations.subscribe(
      designAutomation.eventTypes.AUTOMATION_COMPLETED,
      this.handleAutomationEnd
    );
  }

  componentWillUnmount() {
    this.updateSharedData();

    designAutomation.operations.unsubscribe(
      designAutomation.eventTypes.AUTOMATION_STARTED,
      this.handleAutomationStart
    );
    designAutomation.operations.unsubscribe(
      designAutomation.eventTypes.AUTOMATION_COMPLETED,
      this.handleAutomationEnd
    );
  }

  onSubmit({ isClickOnOverlay } = {}) {
    const {
      onDialogMainAction,
      forceReloadView,
      setCleanUrlsEnabled,
      hideDialog,
      bulkViewActions,
    } = this.props;

    const {
      optionalChildren,
      displayOptions,
      backgroundOption,
      cachedBackgroundOption,
      variationId,
      blockNode,
      blockId,
    } = this.state;

    this.restoreBlockHiddenState();

    transactions.helpers.releaseTransactionSession(transactions.transactionTypes.LIVE_DOCUMENT);

    saveBlockSettingsChanges({
      intermediateBlockId: this.intermediateBlockId,
      destinationBlockNode: this.destinationBlockNode,
      isClickOnOverlay,
      onDialogMainAction,
      optionalChildren,
      displayOptions,
      backgroundOption,
      cachedBackgroundOption,
      variationId,
      hideDialog,
      bulkViewActions,
      originalBlock: this.originalBlock,
      blockNode,
      blockId,
      refId: this.refId,
      forceReloadView,
      setCleanUrlsEnabled,
      initialElement: this.initialElement,
      usedCustomColorOptions: this.usedCustomColorOptions,
      isOnlineStoreBlock: this.isOnlineStoreBlock,
    });
  }

  onCancel() {
    const { bulkSetChildrenPresence, hideDialog, forceReloadView, onDialogCancel } = this.props;
    const {
      blockId,
      blockVariationId,
      staticBlockNode: originalStaticBlockNode,
    } = this.originalBlock;
    const { optionalChildren, blockNode, displayOptions, backgroundOption } = this.state;

    this.restoreBlockHiddenState();

    transactions.helpers.releaseTransactionSession(transactions.transactionTypes.LIVE_DOCUMENT);

    cancelBlockSettingsChanges({
      destinationBlockNode: this.destinationBlockNode,
      isOnlineStoreBlock: this.isOnlineStoreBlock,
      refId: this.refId,
      usedCustomColorOptions: this.usedCustomColorOptions,
      blockId,
      bulkSetChildrenPresence,
      hideDialog,
      forceReloadView,
      onDialogCancel,
      optionalChildren,
      blockNode,
      originalStaticBlockNode,
      displayOptions,
      backgroundOption,
      variationId: blockVariationId,
    });
  }

  setOffsets(modalComponent) {
    const { offsetX: propsOffsetX, offsetY: propsOffsetY } = this.sharedData;
    if (propsOffsetX || propsOffsetY) return;

    const {
      modal: { width, height },
    } = modalComponent.state;

    const { offsetX, offsetY } = dialogs.helpers.getPositionByElement(
      this.blockElement,
      width,
      height
    );

    this.setState({
      offsetX,
      offsetY,
    });
  }

  getUpdatedOptions(data) {
    const newState = {};

    const { isOnlineStoreCleanUrlEnabled } = this.props;

    const { blockNode, displayOptions, backgroundOption, variationId } = this.state;

    if (data.displayOptions) {
      newState.displayOptions = getUpdatedDisplayOptions({
        newDisplayOptions: data.displayOptions,
        oldDisplayOptions: displayOptions,
        blockNode,
        colorPalette: this.colorPalette,
        refId: this.refId,
        switchRadioOption: this.switchRadioOption,
        switchCheckboxOption: this.switchCheckboxOption,
        switchToggleOption: this.switchToggleOption,
        switchSurface: this.switchSurface,
        switchSliderOption: this.switchSliderOption,
        afterSwitchSliderOption: this.afterSwitchSliderOption,
        switchOnlineStoreCategory: this.switchOnlineStoreCategory,
        toggleMultiSurfaces: BlockSettingsContainer.toggleMultiSurfaces,
        modifyDisplayOption: this.modifyDisplayOption,
        isOnlineStoreCleanUrlEnabled,
      });
    }

    // TODO: migrate "fullwidth" attribute to "cw-[100%]" atomic class
    // and cleanup fullwidth display options entirely
    if (
      blockNode.querySelector(CONTAINER_SELECTOR) &&
      !newState.displayOptions?.some((opt) => opt.id === 'fullwidth')
    ) {
      const preservedOption = displayOptions?.find((opt) => opt.id === CONTAINER_WIDTH_OPTION_ID);
      const containerWidthOption = this.buildContainerWidthOption(
        preservedOption || buildContainerWidthOption({ blockNode })
      );
      newState.displayOptions = [...(newState.displayOptions || []), containerWidthOption];
    }

    if (isBackgroundOptionAvailable(blockNode)) {
      const newbackgroundOption = createBackgroundOption({
        blockNode,
        variationId,
        backgroundOption,
      });

      newState.backgroundOption = newbackgroundOption;
      newState.cachedBackgroundOption = newbackgroundOption;
    } else {
      newState.backgroundOption = null;
    }

    return newState;
  }

  setElementAttribute(element, attribute, value) {
    const { setElementAttribute } = this.props;
    const elementId = view.accessors.getLiveElementId(element);

    if (elementId) {
      setElementAttribute(elementId, attribute, value, LIVE_STRATEGY_OPTIONS);
    } else {
      element.setAttribute(attribute, value);
    }
  }

  getCurrentScrollTop() {
    return this.scrollContainerRef.current?.scrollTop || 0;
  }

  restoreScrollPosition(scrollTop = this.preservedScrollTop) {
    if (!this.scrollContainerRef.current || !scrollTop) {
      return;
    }

    this.scrollContainerRef.current.scrollTop = scrollTop;
  }

  withPreservedScrollPosition(scrollTop, callback) {
    return () => {
      requestAnimationFrame(() => {
        this.restoreScrollPosition(scrollTop);
        callback();
      });
    };
  }

  prepareData(data, callback) {
    const { blockNode } = this.state;
    const newState = { ...this.getUpdatedOptions(data) };

    if (data.optionalChildren) {
      newState.optionalChildren = data.optionalChildren.map((item, index) =>
        createOptionalChildren({
          item,
          index,
          blockNode,
          initialElement: this.initialElement,
          onClick: this.switchChildDisplay,
        })
      );
    }

    this.initialElement = view.accessors.getStaticElement(this.refId).cloneNode(true);

    this.setState(
      {
        ...newState,
        timestamp: Date.now(),
      },
      callback
    );
  }

  async switchChildDisplay(isEnabled, child, options = {}) {
    const { setChildrenPresence } = this.props;
    const { blockId: blockIdFromState, blockNode, optionalChildren, variationId } = this.state;

    const blockId = blockIdFromState || this.intermediateBlockId;

    const blockSettings = blocks.accessors.getBlockSettings(blockId, this.colorPalette);

    trackBlockSettingsOptionClicked({
      blockId,
      blockVariationId: variationId,
      blockSettingId: child.id,
    });

    const shouldRegenerateAtomicCss = isEnabled && !child.isEnabled;

    const updatedChildren = optionalChildren.map((optionChild) => {
      if (optionChild.querySelector !== child.querySelector) return optionChild;

      const childrenPresenceUpdate = {
        blockSettingId: optionChild.id,
        childrenSelector: optionChild.querySelector,
        enabled: isEnabled,
      };

      setChildrenPresence(this.refId, childrenPresenceUpdate, LIVE_STRATEGY_OPTIONS);

      return {
        ...optionChild,
        isEnabled,
        highlighted: options.highlighted || false,
        touched: true,
      };
    });

    if (shouldRegenerateAtomicCss) {
      await view.operations.regenerateAtomicCss();
    }

    trackWsBlockContent(blockNode);
    clearTimeout(this.timer);

    this.setState(
      {
        optionalChildren: updatedChildren,
        isDirtyChildren: true,
        ...this.getUpdatedOptions(blockSettings),
        sortBlockVariationId: LIVE_PREVIEW_MOCKED_ID,
        selectedLayoutVariationId: LIVE_PREVIEW_MOCKED_ID,
      },
      () => {
        view.operations.regenerateAtomicCss().then(() => {
          this.scrollToBlock();
        });
      }
    );
  }

  switchRadioOption(option, selectedValue) {
    this.modifyDisplayOption(option, (displayOption) => {
      const { blockNode } = this.state;
      const targetElement = getOptionTargetElement(option, blockNode);
      const prevValue = targetElement.attributes[option.attribute].value;
      const isMultiValue = option.options.some(({ value }) => value !== prevValue);

      if (isMultiValue) {
        const newValue = prevValue.includes(option.value)
          ? prevValue.replace(option.value, selectedValue)
          : `${prevValue} ${selectedValue}`;
        this.setElementAttribute(targetElement, option.attribute, newValue);
      } else {
        this.setElementAttribute(targetElement, option.attribute, selectedValue);
      }

      return {
        ...displayOption,
        value: selectedValue,
      };
    });
  }

  switchOnlineStoreCategory(option, newValue) {
    this.modifyDisplayOption(option, (displayOption) => {
      const { blockNode } = this.state;
      const targetElement = getOptionTargetElement(option, blockNode);

      this.setElementAttribute(targetElement, option.attribute, newValue);

      return {
        ...displayOption,
        value: newValue,
      };
    });
  }

  switchSurface(surfaceDisplayOption, surface) {
    if (surfaceDisplayOption.value === surface) {
      return;
    }

    this.modifyDisplayOption(surfaceDisplayOption, (displayOption) => {
      const { setElementAttribute } = this.props;

      let surfaces = [...displayOption.surfaces];

      if (Number.isInteger(displayOption.index)) {
        surfaces[displayOption.index] = surface;
      } else {
        surfaces = surface.split(' ');
      }

      const surfacesAttributeValue = surfaces.join(' ');

      setElementAttribute(
        this.refId,
        surfaceDisplayOption.attribute,
        surfacesAttributeValue,
        LIVE_STRATEGY_OPTIONS
      );

      return {
        ...displayOption,
        surfaces: surfaces.filter(Boolean),
        value: surface,
      };
    });
  }

  switchSliderOption(option, value) {
    this.modifyDisplayOption(option, (displayOption) => {
      const { blockNode } = this.state;
      const targetElement = getOptionTargetElement(displayOption, blockNode);

      this.setElementAttribute(targetElement, displayOption.attribute, value);

      return {
        ...displayOption,
        value,
      };
    });
  }

  afterSwitchSliderOption(option, value) {
    const { blockNode } = this.state;
    const targetElement = getOptionTargetElement(option, blockNode);

    this.setElementAttribute(targetElement, option.attribute, value);
  }

  switchCheckboxOption(isEnabled, option) {
    clearTimeout(this.timer);

    this.modifyDisplayOption(option, (displayOption) => {
      const { blockNode } = this.state;
      const targetElement = getOptionTargetElement(option, blockNode);
      const displayOptionValue =
        typeof displayOption.value === 'boolean' ? '' : displayOption.value;

      const changedOption = {
        ...displayOption,
        isEnabled,
        value: displayOptionValue,
      };

      const operation = convertChangedOptionToOperation(changedOption, targetElement);

      if (operation.elementId) {
        const action = convertAttributeOperationToAction({
          ...operation,
          options: LIVE_STRATEGY_OPTIONS,
        });

        store.dispatch(action);
      } else if (operation.operation === SET) {
        targetElement.setAttribute(operation.name, operation.value);
      } else {
        targetElement.removeAttribute(operation.name);
      }

      return {
        ...displayOption,
        isEnabled,
      };
    }).then(this.scrollToBlock);
  }

  switchToggleOption(isEnabled, option) {
    clearTimeout(this.timer);

    this.modifyDisplayOption(option, (displayOption) => {
      const { blockNode } = this.state;
      const targetElement = getOptionTargetElement(option, blockNode);

      const changedOption = {
        ...displayOption,
        isEnabled,
        value: displayOption.value,
      };

      const operation = convertChangedOptionToOperation(changedOption, targetElement);

      if (operation.elementId) {
        const action = convertAttributeOperationToAction({
          ...operation,
          options: LIVE_STRATEGY_OPTIONS,
        });

        store.dispatch(action);
      } else if (operation.operation === SET) {
        targetElement.setAttribute(operation.name, operation.value);
      } else {
        targetElement.removeAttribute(operation.name);
      }

      return {
        ...displayOption,
        isEnabled,
      };
    }).then(this.scrollToBlock);
  }

  buildContainerWidthOption(option) {
    return {
      ...option,
      onClick: this.switchContainerWidthValue,
      onChange: this.switchContainerWidthCustomValue,
      onAfterChange: this.commitContainerWidthCustomValue,
    };
  }

  applyContainerWidthLive(classValue) {
    const { setResponsiveClass, removeResponsiveClass } = this.props;

    // TODO: use getBreakpointByDeviceMode() when we add breakpoint-based editing for container width
    const breakpoint = breakpointNames.EMPTY;
    const autofill = null;

    if (classValue) {
      setResponsiveClass(
        this.refId,
        CONTAINER_WIDTH_CLASS,
        classValue,
        breakpoint,
        autofill,
        LIVE_STRATEGY_OPTIONS
      );
    } else {
      removeResponsiveClass(this.refId, CONTAINER_WIDTH_CLASS, breakpoint, LIVE_STRATEGY_OPTIONS);
    }
  }

  switchContainerWidthValue(option, value) {
    this.modifyDisplayOption(option, (displayOption) => {
      const customValue =
        value === CONTAINER_WIDTH_PRESETS.CUSTOM
          ? getInitialContainerWidthCustomValue(displayOption)
          : null;

      const classValue = getContainerWidthClassValue(value, customValue);

      this.applyContainerWidthLive(classValue);

      return {
        ...displayOption,
        value,
        customValue,
      };
    });
  }

  switchContainerWidthCustomValue(option, customValue) {
    const classValue = getContainerWidthClassValue(CONTAINER_WIDTH_PRESETS.CUSTOM, customValue);
    this.applyContainerWidthLive(classValue);
  }

  commitContainerWidthCustomValue(option, customValue) {
    this.modifyDisplayOption(option, (displayOption) => ({
      ...displayOption,
      customValue,
    }));
  }

  modifyDisplayOption(option, callback) {
    const { displayOptions, blockNode, variationId, blockId } = this.state;
    const { addUserModifiedAttribute } = this.props;
    trackBlockSettingsOptionClicked({
      blockId,
      blockVariationId: variationId,
      blockSettingId: option.id,
    });

    let modifiedOption;
    let updatedOptions = displayOptions.map((displayOption) => {
      if (displayOption.id !== option.id) {
        return displayOption;
      }

      modifiedOption = {
        touched: true,
        ...callback(displayOption),
      };

      if (ATTRIBUTES_TO_MARK_AS_USER_MODIFIED.includes(modifiedOption.attribute)) {
        modifiedOption.userModified = true;

        addUserModifiedAttribute(this.refId, modifiedOption.attribute, {
          strategy: updateStrategies.UPDATE_LIVE_ONLY,
        });
      }

      return modifiedOption;
    });

    updatedOptions = updateDependentWhiteSpaceOptions({
      displayOptions: updatedOptions,
      blockNode,
      editedOption: option,
      onChange: this.switchSliderOption,
      onAfterChange: this.afterSwitchSliderOption,
    });

    updatedOptions = updateSurfaceDependentOptions({
      displayOptions: updatedOptions,
      modifiedOption,
    });

    if (this.destinationBlockNode) {
      this.selectedPreviewNode = this.destinationBlockNode.cloneNode(true);
    }

    const scrollTop = this.getCurrentScrollTop();

    return new Promise((resolve) => {
      this.setState(
        {
          displayOptions: updatedOptions,
          isDirtyOptions: true,
          sortBlockVariationId: LIVE_PREVIEW_MOCKED_ID,
          selectedLayoutVariationId: LIVE_PREVIEW_MOCKED_ID,
        },
        this.withPreservedScrollPosition(scrollTop, resolve)
      );
    });
  }

  scrollToBlock(callback = () => {}) {
    this.timer = setTimeout(() => {
      const { blockNode } = this.state;

      scroller.helpers.scrollToElement(blockNode, { includeOffset: false }).then(callback);
    }, 200);
  }

  handleKeyDown(e) {
    if (e.key === ENTER_KEY) {
      this.onSubmit();
    }
  }

  restoreBlockHiddenState() {
    helpers.restoreBlockHiddenState(this.refId, this.hiddenClassTokensSnapshot);

    if (this.destinationBlockNode && this.hiddenClassTokensSnapshot?.length) {
      this.destinationBlockNode.setAttribute(
        'class',
        applyHiddenClassTokens(
          this.destinationBlockNode.getAttribute('class') || '',
          this.hiddenClassTokensSnapshot
        )
      );
    }
  }

  updateSharedData(propsOverwrite = {}) {
    const { resolveSharedData } = this.props;
    const {
      offsetX,
      offsetY,
      selectedLayoutVariationId,
      optionalChildren,
      sortBlockVariationId,
      displayOptions,
      blockNode,
      variationId,
      isDirtyOptions,
      isDirtyChildren,
      blockId,
      backgroundOption,
      cachedBackgroundOption,
    } = this.state;

    resolveSharedData({
      blockId,
      blockNode,
      offsetX,
      offsetY,
      variationId,
      selectedLayoutVariationId,
      isDirtyOptions,
      isDirtyChildren,
      sortBlockVariationId,
      displayOptions,
      backgroundOption,
      cachedBackgroundOption,
      optionalChildren,
      isOnlineStoreBlock: this.isOnlineStoreBlock,
      itemsWithDependencies: this.itemsWithDependencies,
      element: this.blockElement,
      refId: this.refId,
      blockElementId: this.blockElementId,
      compiledNodesCache: this.compiledNodesCache,
      selectedPreviewNode: this.selectedPreviewNode,
      destinationBlockNode: this.destinationBlockNode,
      hiddenClassTokensSnapshot: this.hiddenClassTokensSnapshot,
      optionalChildrenState: optionalChildren,
      blocksPreviewStylesheet: this.blocksPreviewStylesheet,
      originalBlock: this.originalBlock,
      initialElement: this.initialElement,
      scrollTop: this.getCurrentScrollTop(),
      ...propsOverwrite,
    });
  }

  displayBlockLayout() {
    const { blockId, variationId: blockVariationId } = this.state;
    const isReadOnlyModeEnabled = readOnly.verifiers.isReadOnlyModeEnabled();
    if (isReadOnlyModeEnabled) {
      errors.operations.catchError(null, errors.types.READ_ONLY_ERROR);
      return;
    }
    this.updateSharedData();
    dialogs.operations.show(dialogTypes.BLOCK_LAYOUT_DIALOG);
    trackers.trackSwitchBlockLayoutButtonClicked({ blockId, blockVariationId });
  }

  handleDragEnd(_, { x, y }) {
    this.setState({ offsetX: x, offsetY: y });
  }

  preprocessBlockLayoutDecorator(block, params = {}) {
    const {
      optionalChildren,
      displayOptions,
      backgroundOption,
      blockNode,
      cachedBackgroundOption,
      selectedLayoutVariationId,
      sortBlockVariationId,
    } = this.state;

    return preprocessBlockLayout(block, {
      ...params,
      optionalChildrenState: optionalChildren,
      sortBlockVariationId,
      selectedLayoutVariationId,
      displayOptions,
      backgroundOption,
      cachedBackgroundOption,
      refId: this.refId,
      blockNode,
      originalBlockNode: this.originalBlock.blockNode,
      originalStaticBlockNode: this.originalBlock.staticBlockNode,
      compiledNodesCache: this.compiledNodesCache,
      selectedPreviewNode: this.selectedPreviewNode,
      originalVariationId: this.originalBlock.blockVariationId,
    });
  }

  handleAutomationStart() {
    this.setState({
      isAutomationInProgress: true,
    });
  }

  handleAutomationEnd(instructions) {
    const isAutomationApplied = !!instructions.length;

    if (isAutomationApplied || this.shouldPrepareDataAfterEmptyAutomation) {
      this.shouldPrepareDataAfterEmptyAutomation = false;
      const blockSettings = blocks.accessors.getBlockSettings(
        this.intermediateBlockId,
        this.colorPalette
      );
      this.prepareData(blockSettings);
    }

    this.setState({
      isAutomationInProgress: false,
    });
  }

  render() {
    const {
      optionalChildren,
      displayOptions,
      offsetX,
      offsetY,
      blockNode,
      selectedLayoutVariationId,
      timestamp,
      isDirtyOptions,
      tabId,
      backgroundOption,
      variationId,
      isAutomationInProgress,
    } = this.state;

    if (!optionalChildren && !displayOptions) return null;

    const { captions, itemsWithDependencies, category } = this;

    const visibleOptions = getVisibleOptions(displayOptions, blockNode, optionalChildren);

    const itemsToRender = getBlockItemsForRendering({
      blockId: this.intermediateBlockId,
      selectedLayoutVariationId,
      category,
      itemsWithDependencies,
      optionalChildren,
      isDirtyOptions,
      variationId,
      originalBlock: this.originalBlock,
    });

    return (
      <BlockSettingsContext.Provider
        value={{
          blockItems: itemsToRender,
          selectedLayoutVariationId,
          captions,
          options: {
            optionalChildren,
            displayOptions: visibleOptions,
            backgroundOption, // Block background container
          },
          stateDisplayOptions: displayOptions, // Block image container
          originalBlock: this.originalBlock, // Current block layout preview
          displayBlockLayout: this.displayBlockLayout, // Block settings tabs container
          activeTabId: tabId, // Block settings tabs container
          blocksPreviewStylesheet: this.blocksPreviewStylesheet, // Block layout container
          itemPreProcessFunction: this.preprocessBlockLayoutDecorator, // Block layout container
          onSubmit: this.onSubmit, // Modals && block-settings-option
          timestamp, // Block settings tabs container
          updateSharedData: this.updateSharedData,
          scrollContainerRef: this.scrollContainerRef,
        }}
      >
        <BlockSettingsDialogContainer
          offsetX={offsetX}
          offsetY={offsetY}
          onDragEnd={this.handleDragEnd}
          onModalReady={this.setOffsets}
          onKeyDown={this.handleKeyDown}
          onCancel={this.onCancel}
          isAutomationInProgress={isAutomationInProgress}
        />
      </BlockSettingsContext.Provider>
    );
  }
}

BlockSettingsContainer.propTypes = {
  refId: PropTypes.string,
  blockId: PropTypes.string,
  element: PropTypes.object,
  hideDialog: PropTypes.func.isRequired,
  forceReloadView: PropTypes.func.isRequired,
  bulkViewActions: PropTypes.func.isRequired,
  setChildrenPresence: PropTypes.func.isRequired,
  bulkSetChildrenPresence: PropTypes.func.isRequired,
  setCleanUrlsEnabled: PropTypes.func.isRequired,
  blockItems: PropTypes.array.isRequired,
  blockTemplateCollection: PropTypes.array.isRequired,
  isOnlineStoreCleanUrlEnabled: PropTypes.bool.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  tabId: PropTypes.string,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
  setElementAttribute: PropTypes.func.isRequired,
  setResponsiveClass: PropTypes.func.isRequired,
  removeResponsiveClass: PropTypes.func.isRequired,
  addUserModifiedAttribute: PropTypes.func.isRequired,
  optionsSetup: PropTypes.shape({ optionalChildren: PropTypes.shape({}) }),
};

BlockSettingsContainer.defaultProps = {
  tabId: BLOCK_SETTINGS_LAYOUT_TAB_ID,
  element: null,
  blockId: null,
  refId: null,
  optionsSetup: {},
};

const mapDispatchToProps = {
  hideDialog: dialogs.actions.hide,
  setChildrenPresence: view.actions.setChildrenPresence,
  bulkSetChildrenPresence: view.actions.bulkSetChildrenPresence,
  bulkViewActions: view.actions.bulkViewActions,
  forceReloadView: view.actions.forceReloadView,
  setCleanUrlsEnabled: site.actions.setCleanUrlsEnabled,
  setElementAttribute: view.actions.setElementAttribute,
  setResponsiveClass: view.actions.setResponsiveClass,
  removeResponsiveClass: view.actions.removeResponsiveClass,
  addUserModifiedAttribute: designAutomation.actions.addUserModifiedAttribute,
};

const mapStateToProps = (state) => ({
  blockItems: blocks.selectors.getBlocks(state),
  blockTemplateCollection: blockTemplates.selectors.getBlockTemplates(state),
  isOnlineStoreCleanUrlEnabled: site.selectors.getCleanUrlsEnabled(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(BlockSettingsContainer);
